import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-toastify';
import SelectDropdown from '../components/SelectDropdown';
import '../styles/pages/_consultant-wellness-session-creation.scss';

interface Step {
  id: string;
  title: string;
  instructions: string;
  duration: number; // minutes
}

interface SessionFormData {
  title: string;
  description: string;
  category: string;
  duration: number; // minutes
  steps: Step[];
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  videoUrl: string;
  themeColor: string;
  breathingTimer: boolean;
  showProgress: boolean;
}

const ConsultantWellnessSessionCreationPage: React.FC = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id: sessionId } = useParams();

  const [formData, setFormData] = useState<SessionFormData>({
    title: '',
    description: '',
    category: 'Meditation',
    duration: 10,
    steps: [{ id: '1', title: '', instructions: '', duration: 5 }],
    difficulty: 'Beginner',
    videoUrl: '',
    themeColor: '#06B6D4',
    breathingTimer: false,
    showProgress: true,
  });

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const categories = ['Meditation', 'Breathing', 'Movement', 'Visualization', 'Other'];
  const themeColors = [
    { label: 'Teal', value: '#06B6D4' },
    { label: 'Indigo', value: '#6366F1' },
    { label: 'Purple', value: '#A855F7' },
    { label: 'Pink', value: '#EC4899' },
    { label: 'Green', value: '#10B981' },
  ];

  // Calculate progress percentage
  const calculateProgress = (): number => {
    const totalFields = 8;
    let filledFields = 0;

    if (formData.title.trim()) filledFields++;
    if (formData.description.trim()) filledFields++;
    if (formData.category) filledFields++;
    if (formData.duration > 0) filledFields++;
    if (formData.steps.every((s) => s.title && s.instructions)) filledFields++;
    if (formData.difficulty) filledFields++;
    if (formData.themeColor) filledFields++;
    if (formData.steps.length > 0) filledFields++;

    return Math.round((filledFields / totalFields) * 100);
  };

  const handleAddStep = (): void => {
    const newStep: Step = {
      id: Date.now().toString(),
      title: '',
      instructions: '',
      duration: 5,
    };
    setFormData((prev) => ({
      ...prev,
      steps: [...prev.steps, newStep],
    }));
    setCurrentStepIndex((prev) => prev + 1);
  };

  const handleRemoveStep = (stepId: string): void => {
    if (formData.steps.length > 1) {
      setFormData((prev) => ({
        ...prev,
        steps: prev.steps.filter((s) => s.id !== stepId),
      }));
      if (currentStepIndex >= formData.steps.length - 1) {
        setCurrentStepIndex(Math.max(0, currentStepIndex - 1));
      }
    } else {
      toast.error('Session must have at least one step');
    }
  };

  const handlePreviousStep = (): void => {
    setCurrentStepIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextStep = (): void => {
    setCurrentStepIndex((prev) => Math.min(formData.steps.length - 1, prev + 1));
  };

  const handleStepChange = (
    stepId: string,
    field: keyof Step,
    value: string | number
  ): void => {
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps.map((s) =>
        s.id === stepId ? { ...s, [field]: value } : s
      ),
    }));
  };

  const handlePublish = async (): Promise<void> => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a session title');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    if (formData.steps.some((s) => !s.title || !s.instructions)) {
      toast.error('Please complete all steps');
      return;
    }

    try {
      const response = await fetch('/api/wellness-sessions/publish', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          ...formData,
          consultantId: user?.id,
          status: 'published',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to publish session');
      }

      toast.success('Session published successfully!');
      localStorage.removeItem(`wellness_session_draft_${sessionId || 'new'}`);
      navigate('/panel/consultant/wellness');
    } catch (error) {
      toast.error(`Error: ${error instanceof Error ? error.message : 'Failed to publish'}`);
    }
  };

  const handleSaveDraft = (): void => {
    localStorage.setItem(
      `wellness_session_draft_${sessionId || 'new'}`,
      JSON.stringify(formData)
    );
    toast.success('Draft saved');
  };

  const handleCancel = (): void => {
    if (
      Object.values(formData).some(
        (v) => (typeof v === 'string' && v.trim()) || (typeof v === 'number' && v > 0) || (Array.isArray(v) && v.length > 0)
      )
    ) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/consultant/wellness');
      }
    } else {
      navigate('/consultant/wellness');
    }
  };

  const progress = calculateProgress();

  return (
    <div className={`wellness-session-creation ${isDarkMode ? 'dark' : ''}`}>
      <div className="wellness-session-creation__container">
        {/* Header */}
        <div className="wellness-session-creation__header">
          <h1 className="wellness-session-creation__title">Create Wellness Session</h1>
          <p className="wellness-session-creation__subtitle">
            {sessionId ? 'Edit your wellness session' : 'Create a new guided wellness session'}
          </p>
        </div>

        {/* Progress */}
        <div className="wellness-session-creation__progress">
          <div className="wellness-session-creation__progress-header">
            <span className="wellness-session-creation__progress-label">Completion</span>
            <span className="wellness-session-creation__progress-percent">{progress}%</span>
          </div>
          <progress
            className="wellness-session-creation__progress-bar"
            value={progress}
            max={100}
          />
        </div>

        {/* Content */}
        <div className="wellness-session-creation__content">
          {/* Basic Info Section */}
          <div className="wellness-session-creation__section">
            <div className="wellness-session-creation__section-header">
              <h2 className="wellness-session-creation__section-title">
                <span className="material-symbols-outlined">info</span>
                Basic Information
              </h2>
            </div>

            <div className="wellness-session-creation__basic-info-group">
              {/* Session Details */}
              <div className="wellness-session-creation__basic-info-subsection">
                <div className="wellness-session-creation__basic-info-subsection-label">
                  <span className="material-symbols-outlined">edit_note</span>
                  Session Details
                </div>
                <div className="wellness-session-creation__basic-info-content">
                  <div className="form-group">
                    <label htmlFor="title">
                      Session Title
                      <span className="wellness-session-creation__required">*</span>
                    </label>
                    <input
                      id="title"
                      type="text"
                      className="form-input"
                      placeholder="e.g., Morning Meditation"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, title: e.target.value }))
                      }
                      maxLength={100}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="category">Category</label>
                    <SelectDropdown
                      options={categories.map((cat) => ({ label: cat, value: cat }))}
                      value={formData.category}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, category: value }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="wellness-session-creation__basic-info-subsection">
                <div className="wellness-session-creation__basic-info-subsection-label">
                  <span className="material-symbols-outlined">description</span>
                  Overview
                </div>
                <div className="form-group">
                  <label htmlFor="description">
                    Description
                    <span className="wellness-session-creation__required">*</span>
                  </label>
                  <textarea
                    id="description"
                    className="form-input"
                    placeholder="Describe your wellness session..."
                    rows={4}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, description: e.target.value }))
                    }
                    maxLength={500}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Session Settings */}
          <div className="wellness-session-creation__section">
            <div className="wellness-session-creation__section-header">
              <h2 className="wellness-session-creation__section-title">
                <span className="material-symbols-outlined">tune</span>
                Session Settings
              </h2>
            </div>

            <div className="wellness-session-creation__settings-group">
              {/* Core Settings */}
              <div className="wellness-session-creation__settings-subsection">
                <div className="wellness-session-creation__subsection-label">
                  <span className="material-symbols-outlined">schedule</span>
                  Core Settings
                </div>
                <div className="wellness-session-creation__subsection-content">
                  <div className="form-group">
                    <label htmlFor="duration">Duration (minutes)</label>
                    <input
                      id="duration"
                      type="number"
                      className="form-input"
                      placeholder="10"
                      value={formData.duration}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          duration: parseInt(e.target.value) || 0,
                        }))
                      }
                      min={1}
                      max={120}
                    />
                  </div>

                  <div className="form-group">
                    <label>Difficulty Level</label>
                    <div className="wellness-session-creation__difficulty-selector">
                      {['Beginner', 'Intermediate', 'Advanced'].map((level) => (
                        <button
                          key={level}
                          className={`wellness-session-creation__difficulty-btn ${
                            formData.difficulty === level
                              ? 'wellness-session-creation__difficulty-btn--active'
                              : ''
                          }`}
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              difficulty: level as SessionFormData['difficulty'],
                            }))
                          }
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Media Settings */}
              <div className="wellness-session-creation__settings-subsection">
                <div className="wellness-session-creation__subsection-label">
                  <span className="material-symbols-outlined">video_library</span>
                  Media
                </div>
                <div className="wellness-session-creation__subsection-content">
                  <div className="form-group wellness-session-creation__form-grid--single">
                    <label htmlFor="videoUrl">Video URL (optional)</label>
                    <input
                      id="videoUrl"
                      type="url"
                      className="form-input"
                      placeholder="https://example.com/video"
                      value={formData.videoUrl}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, videoUrl: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Session Features */}
              <div className="wellness-session-creation__settings-subsection">
                <div className="wellness-session-creation__subsection-label">
                  <span className="material-symbols-outlined">star</span>
                  Features
                </div>
                <div className="wellness-session-creation__subsection-content">
                  <div className="form-group wellness-session-creation__toggle-group">
                    <label className="wellness-session-creation__checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.breathingTimer}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            breathingTimer: e.target.checked,
                          }))
                        }
                      />
                      <span>Include Breathing Timer</span>
                    </label>
                  </div>

                  <div className="form-group wellness-session-creation__toggle-group">
                    <label className="wellness-session-creation__checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.showProgress}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            showProgress: e.target.checked,
                          }))
                        }
                      />
                      <span>Show Progress</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Steps Section */}
          <div className="wellness-session-creation__section">
            <div className="wellness-session-creation__section-header">
              <h2 className="wellness-session-creation__section-title">
                <span className="material-symbols-outlined">list_alt</span>
                Session Steps
                <span className="wellness-session-creation__required">*</span>
              </h2>
            </div>

            <div className="wellness-session-creation__steps-group">
              <div className="wellness-session-creation__steps-container">
                <div className="wellness-session-creation__steps-label">
                  <span className="material-symbols-outlined">timeline</span>
                  Step by Step Guide
                </div>
                
                <div className="wellness-session-creation__carousel-wrapper">
                  <div className="wellness-session-creation__carousel">
                    {formData.steps.map((step, index) => (
                      <div
                        key={step.id}
                        className={`wellness-session-creation__carousel-item ${
                          index === currentStepIndex ? 'wellness-session-creation__carousel-item--active' : ''
                        }`}
                      >
                        <div className="wellness-session-creation__step-header">
                          <div className="wellness-session-creation__list-number">{index + 1}</div>
                          <div className="wellness-session-creation__step-meta">
                            <input
                              type="text"
                              className="form-input wellness-session-creation__step-title-input"
                              placeholder="Step title"
                              value={step.title}
                              onChange={(e) =>
                                handleStepChange(step.id, 'title', e.target.value)
                              }
                              maxLength={50}
                            />
                            <span className="wellness-session-creation__step-duration">
                              <span className="material-symbols-outlined">schedule</span>
                              <input
                                type="number"
                                className="form-input wellness-session-creation__duration-input"
                                placeholder="5"
                                value={step.duration}
                                onChange={(e) =>
                                  handleStepChange(step.id, 'duration', parseInt(e.target.value) || 0)
                                }
                                min={1}
                                max={60}
                              />
                              <span>min</span>
                            </span>
                          </div>
                          {formData.steps.length > 1 && (
                            <button
                              className="wellness-session-creation__remove-btn"
                              onClick={() => handleRemoveStep(step.id)}
                              title="Remove step"
                            >
                              <span className="material-symbols-outlined">close</span>
                            </button>
                          )}
                        </div>
                        <div className="wellness-session-creation__step-content">
                          <textarea
                            className="form-input"
                            placeholder="Step instructions"
                            rows={3}
                            value={step.instructions}
                            onChange={(e) =>
                              handleStepChange(step.id, 'instructions', e.target.value)
                            }
                            maxLength={300}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {formData.steps.length > 1 && (
                    <div className="wellness-session-creation__carousel-controls">
                      <button
                        className="wellness-session-creation__carousel-btn"
                        onClick={handlePreviousStep}
                        disabled={currentStepIndex === 0}
                        title="Previous step"
                      >
                        <span className="material-symbols-outlined">chevron_left</span>
                      </button>
                      <div className="wellness-session-creation__carousel-dots">
                        {formData.steps.map((_, index) => (
                          <button
                            key={index}
                            className={`wellness-session-creation__carousel-dot ${
                              index === currentStepIndex ? 'wellness-session-creation__carousel-dot--active' : ''
                            }`}
                            onClick={() => setCurrentStepIndex(index)}
                            title={`Go to step ${index + 1}`}
                          />
                        ))}
                      </div>
                      <button
                        className="wellness-session-creation__carousel-btn"
                        onClick={handleNextStep}
                        disabled={currentStepIndex === formData.steps.length - 1}
                        title="Next step"
                      >
                        <span className="material-symbols-outlined">chevron_right</span>
                      </button>
                    </div>
                  )}
                </div>

                <button
                  className="wellness-session-creation__add-list-item"
                  onClick={handleAddStep}
                >
                  <span className="material-symbols-outlined">add</span>
                  Add Step
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="wellness-session-creation__footer">
          <button
            className="btn btn-secondary"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <div className="wellness-session-creation__footer-secondary">
            <button
              className="btn btn-outline"
              onClick={handleSaveDraft}
            >
              Save as Draft
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePublish}
            >
              Publish Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantWellnessSessionCreationPage;
